import React, { useState } from "react";
import { useMatch } from "react-router-dom";

import { GET_FLOORING_VARIATIONS } from "graphql/query/flooring-gql";

import { useBreadCrumbs } from "components/use-hooks";
import { Tables } from "components/layout";
import { Localize, ModalStandard } from "components/service";
import { localizeByKey } from "components/service/localize";
import Icons from "components/icons";
import Flooring from "components/flooring";
import { TFlooring } from "components/flooring/helpers/flooring-types-helper";
import { IFilter } from "components/flooring/helpers/flooring-variation-types-helper";
import { PermissionButton } from "components/permissions";

const flooringBasePath = Flooring.Const.basePath;

interface IFlooringPriceVariationsSubPageProps
{
  flooring: TFlooring | undefined;
}

const FlooringPriceVariationsSubPage: React.FC<IFlooringPriceVariationsSubPageProps> = ({ flooring }) =>
{
  const tableHelper = Flooring.Helpers.VariationTableMain;
  const routeUrl = `${flooringBasePath}/${flooring?.id}/price-variations`;
  const match = useMatch(`${routeUrl}/page/:pageNum`);
  const objOrderBy = [ { column: "ID", order: "DESC" as const } ];
  const filtersStorageKey = `flooringVariationFilters_flooringID-${flooring?.id || 0}`;
  const sortingStorageKey = `flooringVariationSorting_flooringID-${flooring?.id || 0}`;
  const flooringVariationFilters = JSON.parse(
    localStorage.getItem(filtersStorageKey) || "{}"
  );
  const flooringVariationSorting = JSON.parse(
    localStorage.getItem(sortingStorageKey) || "null"
  );

  const breadcrumbs = [
    {
      label: localizeByKey("BREADCRUMBS.Label_Flooring"),
      path: `${flooringBasePath}`,
    },
    {
      label: flooring?.title ?? localizeByKey("VARIABLE.Label_EMPTY"),
      path: `${flooringBasePath}/${flooring?.id}`,
    },
    {
      label: localizeByKey("BREADCRUMBS.Label_PriceVariations"),
      path: routeUrl,
    },
  ];

  useBreadCrumbs(breadcrumbs);

  const flooringID = {
    column: "FLOORING_ID",
    operator: "EQ",
    value: flooring?.id ? parseInt(flooring.id) : 0,
  };

  const [ searchText, setSearchText ] = useState("");
  const [ variables, setVariables ] = useState<Record<string, any>>({});
  const [ filters, setFilters ] = useState<IFilter | undefined>();
  const [ resetSorting, setResetSorting ] = useState<{ column: string; order: "ASC" | "DESC" }[]>();

  const objectWhere = filters
    ? {
      ...flooringID,
      AND: [ filters ],
    }
    : flooringID;

  return (
    <Tables.Main
      model="flooringVariations"
      query={GET_FLOORING_VARIATIONS}
      querySkip={!flooring?.id}
      routeUrl={routeUrl}
      currentPage={match ? parseInt(match.params.pageNum || "1") : 1}
      searchText={searchText}
      objOrderBy={flooringVariationSorting || objOrderBy}
      resetSorting={resetSorting}
      sortingName={sortingStorageKey}
      varSet={{ perPage: 30 }}
      objectWhere={objectWhere}
      tableHelper={tableHelper}
      setVariables={setVariables}
    >
      <div className="table-action-bar">
        <div className="col">
          <Flooring.Filters.VariationTable
            setSearchText={setSearchText}
            setFilters={setFilters}
            setResetSorting={setResetSorting}
            flooringVariationFilters={flooringVariationFilters}
            filters={filters}
            objOrderBy={objOrderBy}
            filtersStorageKey={filtersStorageKey}
            sortingStorageKey={sortingStorageKey}
          />
        </div>

        <div className="col">
          <PermissionButton
            permission={Flooring.Const.permissions.UPDATE}
            disabled={!flooring?.id}
            disabledFallback={true}
            type="primary"
            icon={<Icons.Plus />}
          >
            <ModalStandard
              width={500}
              extraClass={"modal-form"}
              modalButton={
                <span className="ellipsis">
                  <Localize>GLOBAL.Button_Text_CreateNew</Localize>
                </span>
              }
              modalTitle={
                <strong className="modal-title">
                  <span className="text">
                    <Localize>FORMS.Title_CreatePriceVariation</Localize>
                  </span>
                </strong>
              }
            >
              <Flooring.Forms.VariationEdit
                flooringID={flooring?.id ?? ""}
                variables={variables}
                action={() => { }}
              />
            </ModalStandard>
          </PermissionButton>
        </div>
      </div>
    </Tables.Main>
  );
};

export default FlooringPriceVariationsSubPage;
